import { useState, useEffect, useRef } from 'react';
import { BookOpen, Plus, Pencil, Trash2, X, Check, Search, FileText, Video, Mail, Book, Link as LinkIcon, Upload, AlertCircle, Download, Eye, FileSpreadsheet, FileCheck, ChevronUp } from 'lucide-react';
import { Resource, resourcesRef, createDocument, getDocuments, updateDocument, deleteDocument, storage, ref, uploadBytesResumable, getDownloadURL } from '../lib/firebase';
import { Dialog, Disclosure } from '@headlessui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';

const RESOURCE_TYPES = [
  { value: 'brief', label: 'Briefs', icon: FileText },
  { value: 'contract', label: 'Contrats', icon: FileCheck },
  { value: 'invoice', label: 'Factures', icon: FileSpreadsheet },
  { value: 'quote', label: 'Devis', icon: FileSpreadsheet },
  { value: 'media_kit', label: 'Kit média', icon: FileText },
  { value: 'email', label: 'Email type', icon: Mail },
  { value: 'training', label: 'Formation interne', icon: Book },
  { value: 'video', label: 'Vidéo', icon: Video },
  { value: 'other', label: 'Autre', icon: LinkIcon },
] as const;

export default function Resources() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedTypes, setExpandedTypes] = useState<Record<string, boolean>>({});
  const [resourcesLimit, setResourcesLimit] = useState<Record<string, number>>({});

  useEffect(() => {
    // Initialiser les limites pour chaque type de ressource
    const initialLimits: Record<string, number> = {};
    RESOURCE_TYPES.forEach(type => {
      initialLimits[type.value] = 3; // Limite initiale de 3 par type
    });
    setResourcesLimit(initialLimits);
  }, []);

  const handleShowMore = (type: string) => {
    setResourcesLimit(prev => ({
      ...prev,
      [type]: resources.filter(r => r.type === type).length // Afficher toutes les ressources de ce type
    }));
    setExpandedTypes(prev => ({
      ...prev,
      [type]: true
    }));
  };

  const handleShowLess = (type: string) => {
    setResourcesLimit(prev => ({
      ...prev,
      [type]: 3 // Revenir à la limite initiale
    }));
    setExpandedTypes(prev => ({
      ...prev,
      [type]: false
    }));
  };

  const [formData, setFormData] = useState<Partial<Resource>>({
    name: '',
    type: 'brief',
    description: '',
    url: '',
  });

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setIsLoading(true);
      const data = await getDocuments<Resource>(resourcesRef);
      setResources(data);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.url && !selectedFile && !selectedResource?.fileUrl) {
      setError('Veuillez fournir soit une URL, soit un fichier.');
      return;
    }

    try {
      let fileUrl = '';
      let fileName = '';
      let fileType = '';
      let fileSize = 0;

      if (selectedFile) {
        setIsUploading(true);
        const storageRef = ref(storage, `resources/${Date.now()}_${selectedFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, selectedFile);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              reject(error);
            },
            async () => {
              fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
              fileName = selectedFile.name;
              fileType = selectedFile.type;
              fileSize = selectedFile.size;
              resolve();
            }
          );
        });
      }

      const resourceData = {
        ...formData,
        ...(fileUrl && {
          fileUrl,
          fileName,
          fileType,
          fileSize,
        }) || (selectedResource?.fileUrl && !selectedFile && {
          fileUrl: selectedResource.fileUrl,
          fileName: selectedResource.fileName,
          fileType: selectedResource.fileType,
          fileSize: selectedResource.fileSize,
        }),
      };

      if (selectedResource?.id) {
        await updateDocument(resourcesRef, selectedResource.id, resourceData);
      } else {
        await createDocument(resourcesRef, resourceData as Resource);
      }

      await loadResources();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving resource:', error);
      setError('Une erreur est survenue lors de la sauvegarde de la ressource.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (!selectedResource?.id) return;
      await deleteDocument(resourcesRef, selectedResource.id);
      await loadResources();
      setIsDeleteModalOpen(false);
      setSelectedResource(null);
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
  };

  const handleEdit = (resource: Resource) => {
    setSelectedResource(resource);
    setFormData({
      name: resource.name,
      type: resource.type,
      description: resource.description,
      url: resource.url || '',
    });
    setIsModalOpen(true);
  };

  const handleView = (resource: Resource) => {
    setSelectedResource(resource);
    setIsViewModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedResource(null);
    setFormData({
      name: '',
      type: 'brief',
      description: '',
      url: '',
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setError('');
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setFormData(prev => ({
      ...prev,
      name: prev.name || file.name.split('.')[0],
    }));
  };

  const formatDate = (date: Date | { seconds: number, nanoseconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = 
      resource.name.toLowerCase().includes(search.toLowerCase()) ||
      resource.description.toLowerCase().includes(search.toLowerCase());
    
    const matchesType = typeFilter === 'all' || resource.type === typeFilter;

    return matchesSearch && matchesType;
  });

  const getResourceTypeIcon = (type: string) => {
    const resourceType = RESOURCE_TYPES.find(t => t.value === type);
    const Icon = resourceType?.icon || LinkIcon;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Ressources</h1>
            <p className="text-gray-400">Gérez vos ressources internes</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter une ressource
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une ressource..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 bg-white border-gray-200 text-gray-900"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {RESOURCE_TYPES.map((resourceType) => {
            const resourcesOfType = resources.filter(r => 
              r.type === resourceType.value && 
              (r.name.toLowerCase().includes(search.toLowerCase()) || 
               r.description.toLowerCase().includes(search.toLowerCase()))
            );
            const displayedResources = resourcesOfType.slice(0, resourcesLimit[resourceType.value] || 3);
            const hasMoreToShow = resourcesOfType.length > (resourcesLimit[resourceType.value] || 3);
            const isExpanded = expandedTypes[resourceType.value] || false;
            const Icon = resourceType.icon;

            return (
              <div key={resourceType.value} className="bg-gray-50 rounded-xl p-4 border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900">{resourceType.label}</h3>
                  </div>
                  <button
                    onClick={() => {
                      setFormData(prev => ({ ...prev, type: resourceType.value }));
                      setIsModalOpen(true);
                    }}
                    className="p-1 text-indigo-600 hover:text-indigo-700"
                    title={`Ajouter un ${resourceType.label.toLowerCase()}`}
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2 mb-2">
                  {displayedResources.map((resource) => (
                    <div
                      key={resource.id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-gray-100 transition-colors group border border-gray-200"
                    >
                      <div className="flex-1 min-w-0">
                        {resource.fileUrl ? (
                          <a
                            href={resource.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-gray-900 hover:text-indigo-600 group relative"
                            title={resource.name}
                          >
                            <span className="truncate">{resource.name}</span>
                            <span className="text-sm text-gray-500 flex-shrink-0">
                              ({formatFileSize(resource.fileSize || 0)})
                            </span>
                            <div className="absolute -top-8 left-0 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {resource.name}
                            </div>
                          </a>
                        ) : resource.url ? (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-gray-900 hover:text-indigo-600 group relative"
                            title={resource.name}
                          >
                            <span className="truncate">{resource.name}</span>
                            <LinkIcon className="w-4 h-4 flex-shrink-0" />
                            <div className="absolute -top-8 left-0 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {resource.name}
                            </div>
                          </a>
                        ) : (
                          <span className="text-gray-900 truncate group relative" title={resource.name}>
                            {resource.name}
                            <div className="absolute -top-8 left-0 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                              {resource.name}
                            </div>
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleView(resource)}
                          className="p-1 text-gray-500 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(resource)}
                          className="p-1 text-gray-500 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedResource(resource);
                            setIsDeleteModalOpen(true);
                          }}
                          className="p-1 text-red-600 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {resourcesOfType.length === 0 && (
                    <div className="text-center py-6 text-gray-500">
                      Aucun {resourceType.label.toLowerCase()}
                    </div>
                  )}
                </div>
                
                {resourcesOfType.length > 3 && (
                  <div className="text-center">
                    {!isExpanded && hasMoreToShow ? (
                      <button 
                        onClick={() => handleShowMore(resourceType.value)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center justify-center mx-auto gap-1"
                      >
                        <span>Voir {resourcesOfType.length - displayedResources.length} de plus</span>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    ) : isExpanded && (
                      <button 
                        onClick={() => handleShowLess(resourceType.value)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center justify-center mx-auto gap-1"
                      >
                        <span>Réduire</span>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* View Resource Modal */}
      <Dialog
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border border-gray-200 shadow-lg">
            {selectedResource && (
              <>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <div className="text-indigo-600">
                        {getResourceTypeIcon(selectedResource.type)}
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedResource.name}</h2>
                      <p className="text-gray-600">
                        {RESOURCE_TYPES.find(t => t.value === selectedResource.type)?.label}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedResource.description}</p>
                  </div>

                  <div className="space-y-4">
                    {selectedResource.url && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">URL</h3>
                        <a
                          href={selectedResource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 break-all"
                        >
                          {selectedResource.url}
                        </a>
                      </div>
                    )}

                    {selectedResource.fileUrl && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Fichier</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-indigo-600" />
                            <span className="text-gray-700 truncate">{selectedResource.fileName}</span>
                            <span className="text-sm text-gray-500">
                              ({formatFileSize(selectedResource.fileSize || 0)})
                            </span>
                          </div>
                          <a
                            href={selectedResource.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                          >
                            <Download className="w-5 h-5" />
                            <span>Télécharger</span>
                          </a>
                        </div>
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Informations</h3>
                      <div className="space-y-2 text-gray-700">
                        <p>Créé le {formatDate(selectedResource.createdAt)}</p>
                        <p>Dernière modification le {formatDate(selectedResource.updatedAt)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleEdit(selectedResource);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => setIsViewModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border-2 border-black">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-gray-600 hover:text-gray-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {selectedResource ? 'Modifier la ressource' : 'Ajouter une ressource'}
                </h2>

                {error && (
                  <div className="mb-6 bg-red-50 border-2 border-red-500 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Nom de la ressource
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full bg-white border-2 border-black text-gray-900"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      id="type"
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as Resource['type'] })}
                      className="mt-1 block w-full bg-white border-2 border-black text-gray-900"
                      required
                    >
                      {RESOURCE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full bg-white border-2 border-black text-gray-900"
                      required
                    />
                  </div>

                  {/* File Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors bg-white ${
                      isDragging
                        ? 'border-accent-blue bg-accent-blue/5'
                        : 'border-black hover:border-accent-blue/50'
                    }`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                    />
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-accent-blue" />
                      {selectedFile ? (
                        <div className="text-gray-900">
                          <p className="font-medium">{selectedFile.name}</p>
                          <p className="text-sm text-gray-600">
                            {formatFileSize(selectedFile.size)}
                          </p>
                        </div>
                      ) : (
                        <>
                          <p className="text-gray-900 font-medium">
                            Glissez-déposez un fichier ici ou cliquez pour sélectionner
                          </p>
                          <p className="text-sm text-gray-600">
                            PDF, Word, Excel, Images, etc.
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                      URL ou lien (optionnel)
                    </label>
                    <input
                      type="url"
                      id="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      className="mt-1 block w-full bg-white border-2 border-black text-gray-900"
                      placeholder="https://"
                    />
                  </div>

                  {isUploading && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">Téléchargement en cours...</span>
                        <span className="text-sm text-gray-700">{uploadProgress.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-accent-blue h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-black rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 border-2 border-black rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  {selectedResource ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-md w-full mx-4 p-6 border border-dark-200">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white">Confirmer la suppression</h3>
              <p className="mt-2 text-sm text-gray-400">
                Êtes-vous sûr de vouloir supprimer la ressource "{selectedResource?.name}" ? Cette action est irréversible.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-lg hover:bg-dark-300"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}